import { BaseTableEntry, StoreType } from './TypeDefinitions';
import { storageConstants } from './AuthConstants';
import { logConstants } from '../utils/Constants';
import { Logger } from './Logger';
import FDBFactory from 'fake-indexeddb/lib/FDBFactory';
import { config } from './AuthConfig';
import { AuthStorage } from './AuthStorage';
import AuthMan from './AuthMan';

export class AuthStorageWrapper {
	private inMemoryStorage: AuthStorage;
	private persistentStorage: AuthStorage;

	private static instance: AuthStorageWrapper;
	public static async getInstance(): Promise<AuthStorageWrapper> {
		if (!AuthStorageWrapper.instance) {
			const authStorageWrapperInstance = new AuthStorageWrapper();
			try {
				await authStorageWrapperInstance.init();
			} catch (errorObj) {
				const errorCode = (errorObj as { errorCode: number }).errorCode;
				Logger.getInstance().write(
					logConstants.INFO,
					`Auth DB initialization failed, errorCode: ${errorCode},  ${errorObj.message}`
				);
				throw errorObj;
			}
			AuthStorageWrapper.instance = authStorageWrapperInstance;
		}
		return AuthStorageWrapper.instance;
	}

	getInMemoryStorage(): AuthStorage {
		return this.inMemoryStorage;
	}

	getPersistentStorage(): AuthStorage {
		return this.persistentStorage;
	}

	/**
	 * Returns persistent or in-memory AuthStorage instance based on referenceUri and config.saveTokens .
	 *
	 * referenceUri is an optional parameter, for cases when storage does not depend on whether the store is cloud or on-prem.
	 * If no referenceUri is input, or referenceUri is for a cloud store, persistent AuthStorage is returned if config.saveTokens is true, else in-memory AuthStorage is returned.
	 * For on-prem store, in-memory AuthStorage is always returned.
	 * If store configuration is not set, for example during gateway store addition, in-memory AuthStorage is returned.
	 */
	getAuthStorage(referenceUri?: string): AuthStorage {
		if (!config.isUnifiedAuthenticationEnabled || !referenceUri) {
			return config.saveTokens ? this.persistentStorage : this.inMemoryStorage;
		}

		const storeConfig = AuthMan.getInstance().getStoreConfiguration();
		if (storeConfig[referenceUri]?.storeType === StoreType.Cloud) {
			return config.saveTokens ? this.persistentStorage : this.inMemoryStorage;
		}

		return this.inMemoryStorage;
	}

	private async init(): Promise<void> {
		this.inMemoryStorage = await AuthStorage.createInstance(new FDBFactory());
		this.persistentStorage = await AuthStorage.createInstance(window.indexedDB);
	}

	/**
	 * Returns first matching entry/row from given tableName in persistent DB.
	 * If no entry found, returns first matching entry/row from given tableName in-memory DB.
	 *
	 * searchWith parameter can be "key" or "index". Default value is "key"
	 *searchFor parameter is the value of key or index corresponding to the searchWith parameter
	 * indexName is an optional parameter. if searchWith="index", indexName must be provided
	 */
	async readEntry(
		tableName: string,
		searchFor: string | string[],
		searchWith = storageConstants.KEY,
		indexName?: string
	): Promise<BaseTableEntry | null> {
		let entry = await this.persistentStorage.readEntry(
			tableName,
			searchFor,
			searchWith,
			indexName
		);
		if (!entry) {
			entry = await this.inMemoryStorage.readEntry(
				tableName,
				searchFor,
				searchWith,
				indexName
			);
		}
		return entry;
	}

	/**
	 * Returns all matching entries/rows from given tableName in both persistent and in-memory DBs.
	 */
	async readAll(
		tableName: string,
		indexName?: string,
		indexValue?: string | string[]
	): Promise<BaseTableEntry[]> {
		return [
			...(await this.inMemoryStorage.readAll(tableName, indexName, indexValue)),
			...(await this.persistentStorage.readAll(tableName, indexName, indexValue)),
		];
	}

	async clearObjectStores(objectStoreNames: string[]): Promise<void> {
		await this.inMemoryStorage.clearObjectStores(objectStoreNames);
		await this.persistentStorage.clearObjectStores(objectStoreNames);
	}

	async clearAllObjectStores(): Promise<void> {
		await this.inMemoryStorage.clearAllObjectStores();
		await this.persistentStorage.clearAllObjectStores();
	}
}
